/**
 * Распознаёт отказ шлюза «твой запрос не соответствует схеме».
 *
 * Зачем: веб-бандл запечён в APK, поэтому версия приложения = версия GraphQL-документов.
 * Когда они расходятся со схемой шлюза, отклоняется **весь** документ на фазе валидации —
 * ломается не одно поле, а целая функция. Так уехал versionCode 11: документ просил
 * `Post.createdSessionId`, которого на проде не было, и создание сбора падало целиком.
 *
 * Почему это надо ловить отдельно: такой отказ приходит с **HTTP 200** и `errors[]`, где нет
 * привычного `errorCode`. `gqlFetcher` доходил до общего throw, статус 200 не входит в список
 * «сообщение уже написано для человека» — и человек получал безликое «что-то пошло не так».
 *
 * Почему нельзя ловить по одной классификации: `ValidationError` в graphql-java покрывает и
 * приведение переменных («Variable 'input' has an invalid value…»), то есть обычный баг формы.
 * Поэтому нужен ещё и тип правила валидации из сообщения — оно у graphql-java генерируется
 * по стабильному шаблону `Validation error of type <RULE>: …`.
 *
 * И главное: сам по себе этот признак НИКОГДА не включает экран обновления. Он влияет только
 * на текст ошибки; что показать — баннер, блок или ничего — решает сравнение версии с
 * `/app/status.json`. Иначе свежая сборка с обычным багом отправляла бы человека обновляться
 * туда, где обновляться не на что.
 */

/**
 * Правила валидации graphql-java, которые означают именно расхождение контракта: клиент просит
 * то, чего в схеме нет, или не знает про обязательное. Список намеренно узкий и сверен с
 * `ValidationErrorType` из графql-java (в jar 25.0 — именно `UndefinedFragment`, не «Unknown»).
 */
const DRIFT_RULES = [
  'FieldUndefined',
  'UnknownType',
  'UnknownArgument',
  'UnknownDirective',
  'UnknownOperation',
  'UndefinedFragment',
  'MissingFieldArgument',
  'MissingDirectiveArgument',
  'SubselectionRequired',
  'SubselectionNotAllowed',
  'InvalidFragmentType',
  'FragmentTypeConditionInvalid',
  'NonInputTypeOnVariable',
  'NonExecutableDefinition',
  'UndefinedVariable',
] as const;

/**
 * Два формата сообщения, и оба обязательны.
 *
 * Живой шлюз (graphql-java 21+) пишет `Validation error (FieldUndefined@[user/foo]) : …` —
 * это проверено запросом к проду и деву. Путь в скобках есть не у всех правил (`UnknownType`
 * идёт без него), поэтому он необязателен.
 *
 * Второй вариант, `Validation error of type FieldUndefined: …`, — формат graphql-java < 17.
 * Он здесь не «на всякий случай»: сначала было написано только по нему, ветка молча не
 * срабатывала ни разу, и весь разбор «клиент устарел» оказался мёртвым кодом при зелёных
 * тестах — тесты кодировали тот же несуществующий формат. Оставлен, чтобы откат шлюза на
 * старую версию не воспроизвёл ту же тишину.
 */
const RULES = DRIFT_RULES.join('|');
const DRIFT_RULE_PATTERN = new RegExp(
  `Validation error (?:\\((?:${RULES})(?:@\\[|\\))|of type (?:${RULES})\\b)`
);

export type GraphqlErrorLike = {
  message?: string;
  extensions?: { classification?: string } | null;
};

export const isSchemaDriftError = (error: GraphqlErrorLike | undefined | null): boolean => {
  if (!error) {
    return false;
  }
  const classification = error.extensions?.classification;
  // Разбор запроса вообще не удался — документ несовместим на уровне синтаксиса/операции.
  // Такое от бага в переменных прийти не может, поэтому достаточно классификации.
  if (classification === 'InvalidSyntax' || classification === 'OperationNotSupported') {
    return true;
  }
  if (classification !== 'ValidationError') {
    return false;
  }
  return DRIFT_RULE_PATTERN.test(error.message ?? '');
};

/** Код, которым помечается такой отказ в `ApiError`. Разбирается в `queryClient`. */
export const CLIENT_OUTDATED_CODE = 'client.outdated';
